import { ActionIcon, Box, Button, Drawer, Skeleton, Text, Tooltip } from "@mantine/core";
import {
  ArrowsLeftRightIcon,
  BankIcon,
  CalendarPlusIcon,
  CoinsIcon,
  CreditCardIcon,
  MoneyIcon,
  QrCodeIcon,
  XIcon,
} from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { SettlementInvoiceApi } from "../../apis";
import { currencyFormat } from "../../utils";
import { renderBusinessName } from "../../utils/render-business-name";
import { renderNameTitleCase } from "../../utils/render-name";
import { ubFormat } from "../../utils/ub-time";
import classes from "./invoice-peek.module.css";

export const peekKey = (id: string) => `settlement.invoice.peek.${id}`;

const METHOD_ICONS: Record<string, ReactNode> = {
  BARTER: <ArrowsLeftRightIcon size={16} weight="bold" />,
  BANK_ACCOUNT: <BankIcon size={16} weight="bold" />,
  B2B: <BankIcon size={16} weight="bold" />,
  BANK_CARD: <CreditCardIcon size={16} weight="bold" />,
  CASH: <MoneyIcon size={16} weight="bold" />,
  QPAY: <QrCodeIcon size={16} weight="bold" />,
};

const DOT_COLORS: Record<string, string> = {
  CONFIRMED: "var(--mantine-color-gray-7)",
  PAYMENT: "var(--mantine-color-green-6)",
  EXTENSION: "var(--mantine-color-gray-7)",
  AMENDED: "var(--mantine-color-gray-6)",
  OVERDUE: "var(--mantine-color-red-6)",
};

export type PeekInvoice = { id: string; refCode: string };

type ViewProps = {
  id: string;
  onClose: () => void;
  onExtend: (invoice: PeekInvoice) => void;
};

function PeekView({ id, onClose, onExtend }: ViewProps) {
  const { t } = useTranslation(["settlement", "common"]);
  const { data, error, isLoading } = useSWR(peekKey(id), () => SettlementInvoiceApi.peek(id), { revalidateOnFocus: false });

  const money = (v?: number | null) => currencyFormat(Number(v ?? 0), { withSymbol: false });

  if (isLoading) {
    return (
      <Box className={classes.root}>
        <Box className={classes.state}>
          <Skeleton h={24} w={220} mb="md" />
          <Skeleton h={96} mb="md" />
          <Skeleton h={64} mb="md" />
          <Skeleton h={120} />
        </Box>
      </Box>
    );
  }

  const inv = data?.invoice;
  if (error || !inv) {
    return (
      <Box className={classes.root}>
        <Box className={classes.state}>
          <Text c="dimmed">{t(error ? "settlement:peek.loadError" : "settlement:peek.notFound")}</Text>
        </Box>
      </Box>
    );
  }

  const ext = data.extension as { count: number; max: number; canExtend: boolean };
  const payments: any[] = data.payments ?? [];
  const timeline: any[] = data.timeline ?? [];

  const closed = inv.paymentStatus === "CLOSED" || inv.invoiceStatus === "CLOSED" || Number(inv.amountToPay) <= 0;
  const days: number | null = inv.overdueDays;
  let pill = { cls: classes.pillNeutral, text: "" };
  if (closed) pill = { cls: classes.pillNeutral, text: t("settlement:detail.inv.closed") };
  else if (days != null && days > 0) pill = { cls: classes.pillDanger, text: t("settlement:detail.inv.overdue", { count: days }) };
  else if (days != null && days < 0) pill = { cls: classes.pillNeutral, text: t("settlement:detail.inv.remaining", { count: -days }) };
  else if (days === 0) pill = { cls: classes.pillWarn, text: t("settlement:detail.inv.dueToday") };

  const confirmed = Number(inv.confirmedAmount) || 0;
  const pct = confirmed > 0 ? Math.min(100, Math.round((Number(inv.paidAmount) / confirmed) * 100)) : 0;
  const invoicePath = inv.side === "SENDER" ? `/sales-invoice/${inv.id}/details` : `/buyer-invoice/${inv.id}/details`;

  const extendBlock = !ext.canExtend
    ? ext.count >= ext.max
      ? t("settlement:peek.extendLimit")
      : Number(inv.amountToPay) <= 0
        ? t("settlement:peek.extendPaid")
        : t("settlement:peek.extendStatus")
    : null;

  const methodLabel = (code?: string | null) => (code ? t(`settlement:peek.method.${code}`, { defaultValue: code }) : "-");

  const eventText = (e: any) => {
    switch (e.kind) {
      case "CONFIRMED":
        return t("settlement:peek.timeline.CONFIRMED", { amount: money(e.amount) });
      case "PAYMENT":
        return t("settlement:peek.timeline.PAYMENT", { method: methodLabel(e.method), amount: money(e.amount) });
      case "OVERDUE":
        return e.days > 1 ? t("settlement:peek.timeline.OVERDUE_DAYS", { count: e.days }) : t("settlement:peek.timeline.OVERDUE");
      default:
        return e.description || t(`settlement:peek.timeline.${e.kind}`);
    }
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.head}>
        <Box className={classes.headTop}>
          <Box className={classes.headTitle}>
            <Text span className={classes.number}>{inv.refCode}</Text>
            {pill.text && <Text span className={`${classes.pill} ${pill.cls}`}>{pill.text}</Text>}
          </Box>
          <ActionIcon variant="subtle" color="gray" size={32} onClick={onClose} aria-label={t("settlement:peek.close")}>
            <XIcon size={18} weight="bold" />
          </ActionIcon>
        </Box>
        <Box className={classes.meta}>
          <Text span className={classes.metaName}>{renderBusinessName(inv.counterparty, "-")}</Text>
          <Text span className={classes.sep} aria-hidden>|</Text>
          <Text span>{t("settlement:peek.confirmedAt", { date: ubFormat(inv.confirmedDate, "MM-DD HH:mm") })}</Text>
          <Text span className={classes.sep} aria-hidden>|</Text>
          <Text span>
            {t("settlement:peek.dueOn")} <Text span className={classes.metaStrong}>{inv.dueDay ?? "-"}</Text>
          </Text>
        </Box>
      </Box>

      <Box className={classes.body}>
        <Box component="section" className={classes.summary}>
          <Box className={classes.amounts}>
            <Box className={classes.amount}>
              <Text span className={classes.amountLabel}>{t("settlement:peek.invoiced")}</Text>
              <Text span className={classes.amountValue}>{money(inv.confirmedAmount)}</Text>
            </Box>
            <Box className={classes.amount}>
              <Text span className={classes.amountLabel}>{t("settlement:peek.paid")}</Text>
              <Text span className={`${classes.amountValue} ${classes.amountPaid}`}>{money(inv.paidAmount)}</Text>
            </Box>
            <Box className={classes.amount}>
              <Text span className={classes.amountLabel}>{t("settlement:peek.remaining")}</Text>
              <Text span className={`${classes.amountValue} ${classes.amountRest}`}>{currencyFormat(Number(inv.amountToPay))}</Text>
            </Box>
          </Box>
          <Box>
            <Box className={classes.bar} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <Box className={classes.barFill} style={{ width: `${pct}%` }} />
            </Box>
            <Text span className={classes.barNote} display="block" mt={6}>
              {t("settlement:peek.progress", { pct, count: ext.count, max: ext.max })}
            </Text>
          </Box>
        </Box>

        <Box component="section" className={classes.section}>
          <h2 className={classes.sectionTitle}>{t("settlement:peek.payments", { count: payments.length })}</h2>
          {payments.length === 0 ? (
            <Box className={classes.empty}>{t("settlement:peek.noPayments")}</Box>
          ) : (
            payments.map((p) => (
              <Box key={p.id} className={classes.payment}>
                <Box className={classes.paymentIcon}>{METHOD_ICONS[p.method] ?? <CoinsIcon size={16} weight="bold" />}</Box>
                <Box className={classes.paymentText}>
                  <Text span className={classes.paymentMethod}>{methodLabel(p.method)}</Text>
                  <Text span className={classes.paymentSub}>
                    {[ubFormat(p.at, "MM-DD HH:mm"), p.actionUser ? renderNameTitleCase(p.actionUser) : null].filter(Boolean).join(" · ")}
                    {p.refCode ? (
                      <>
                        {" · "}
                        {t("settlement:peek.ref")} <Text span className={classes.mono}>{p.refCode}</Text>
                      </>
                    ) : null}
                  </Text>
                </Box>
                <Text span className={classes.paymentAmount}>{money(p.amount)}</Text>
              </Box>
            ))
          )}
        </Box>

        <Box component="section" className={classes.section}>
          <h2 className={classes.sectionTitle}>{t("settlement:peek.history")}</h2>
          <ol className={classes.timeline}>
            {timeline.map((e, i) => (
              <li key={e.id} className={classes.event}>
                <Box className={classes.rail}>
                  <Box component="span" className={classes.dot} style={{ ["--dot" as string]: DOT_COLORS[e.kind] }} />
                  {i < timeline.length - 1 && <Box component="span" className={classes.line} />}
                </Box>
                <Box className={classes.eventBody}>
                  <Text span className={classes.eventText}>{eventText(e)}</Text>
                  <Text span className={classes.eventDate}>{ubFormat(e.at, e.kind === "OVERDUE" ? "MM-DD" : "MM-DD HH:mm")}</Text>
                </Box>
              </li>
            ))}
          </ol>
        </Box>
      </Box>

      <Box className={classes.footer}>
        <Tooltip label={extendBlock} disabled={!extendBlock} withArrow>
          <span>
            <Button
              variant="default"
              disabled={!ext.canExtend}
              leftSection={<CalendarPlusIcon size={16} weight="bold" />}
              onClick={() => onExtend({ id: inv.id, refCode: inv.refCode })}
            >
              {t("settlement:peek.extend")}
            </Button>
          </span>
        </Tooltip>
        <Button component={Link} to={invoicePath} color="dark">
          {t("settlement:peek.open")}
        </Button>
      </Box>
    </Box>
  );
}

type DrawerProps = {
  invoiceId: string | null;
  onClose: () => void;
  onExtend: (invoice: PeekInvoice) => void;
};

export function InvoicePeekDrawer({ invoiceId, onClose, onExtend }: DrawerProps) {
  const { t } = useTranslation("settlement");
  return (
    <Drawer
      opened={!!invoiceId}
      onClose={onClose}
      position="right"
      size={560}
      withCloseButton={false}
      padding={0}
      title={null}
      aria-label={t("peek.title")}
      styles={{ content: { display: "flex", flexDirection: "column" }, body: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: 0 } }}
    >
      {invoiceId && <PeekView key={invoiceId} id={invoiceId} onClose={onClose} onExtend={onExtend} />}
    </Drawer>
  );
}
